import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Typography } from 'antd';

const { Text } = Typography;

interface TurnTimerProps {
  turnKey: string; // A key that changes every turn, e.g., `${round}-${turn}`
}

/**
 * Formats elapsed seconds as MM:SS.
 * Defined outside component to avoid recreation on each render.
 */
const formatTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Displays a timer that tracks elapsed time for the current combat turn.
 * Resets when the turn changes (turnKey prop changes).
 *
 * Refactored following ADR-003 patterns:
 * - Combined turn reset and interval setup into a single effect for cleaner cleanup
 * - Using useCallback for the toggle handler to prevent re-renders
 * - Using ref for interval ID to avoid stale closure issues
 */
const TurnTimer: React.FC<TurnTimerProps> = ({ turnKey }) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);

  // Helper to clear the interval safely
  const clearTimerInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Combined effect: handles turn changes, pause state, and interval management.
  // When turnKey changes, both the timer resets and the interval restarts.
  // When isPaused changes, the interval is started/stopped accordingly.
  useEffect(() => {
    // Reset timer when turn changes
    setElapsedSeconds(0);
    setIsPaused(false);

    // Start the interval (will be paused if user pauses, but starts fresh on turn change)
    intervalRef.current = window.setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    // Cleanup: clear interval when turn changes or component unmounts
    return clearTimerInterval;
  }, [turnKey, clearTimerInterval]);

  // Separate effect for pause/resume to avoid resetting elapsed time
  useEffect(() => {
    if (isPaused) {
      clearTimerInterval();
    } else {
      // Only start if not already running (avoids duplicate intervals)
      intervalRef.current ??= window.setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }

    return clearTimerInterval;
  }, [isPaused, clearTimerInterval]);

  // Stable toggle handler using useCallback
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  return (
    <div className="turn-timer-react">
      <Text className="timer-display-react">{formatTime(elapsedSeconds)}</Text>
      <Button size="small" onClick={togglePause}>
        {isPaused ? 'Resume' : 'Pause'}
      </Button>
    </div>
  );
};

export default TurnTimer;